import { useEffect, useRef, useState } from 'react';
import { useForm, useFormState } from 'react-hook-form';
import { GenericForm } from 'src/components/GenericForm/generic-form';
import { LandingPageTabs } from 'src/pages/Settings/memberships/components/landing-page-tab/landing-page-tabs';
import type { LandingPageFormValues } from 'src/pages/Settings/memberships/components/landing-page-tab/types';
import {
  type ConfigFormSaveSlots,
  EMPTY_CONFIG_FORM_SUBMIT_STATE,
  LandingPageTabValues,
} from 'src/pages/Settings/memberships/types';
import { getLandingPageErrorTab } from 'src/pages/Settings/memberships/utils/get-landing-page-error-tab';

type LandingPageConfigViewFormProps = ConfigFormSaveSlots<LandingPageFormValues> & {
  data: LandingPageFormValues;
  readOnly: boolean;
};

export const LandingPageConfigViewForm = ({
  data,
  readOnly,
  onSaveRequestChange,
  onDirtyChange,
  onSubmitStateChange,
}: LandingPageConfigViewFormProps) => {
  const formMethods = useForm<LandingPageFormValues>({
    defaultValues: data,
    mode: 'onChange',
  });
  const { isDirty, isValid, isValidating, isSubmitting } = useFormState({
    control: formMethods.control,
  });

  const [activeTab, setActiveTab] = useState<LandingPageTabValues>(
    LandingPageTabValues.Content,
  );

  const seededData = useRef(data);

  useEffect(() => {
    if (seededData.current === data || isDirty) {
      return;
    }

    seededData.current = data;
    formMethods.reset(data);
  }, [data, formMethods, isDirty]);

  useEffect(() => {
    return () => {
      onSaveRequestChange(null);
      onDirtyChange(false);
      onSubmitStateChange(EMPTY_CONFIG_FORM_SUBMIT_STATE);
    };
  }, [onSaveRequestChange, onDirtyChange, onSubmitStateChange]);

  useEffect(() => {
    onDirtyChange(isDirty);
  }, [isDirty, onDirtyChange]);

  useEffect(() => {
    onSubmitStateChange({ isValid, isValidating, isSubmitting });
  }, [isSubmitting, isValid, isValidating, onSubmitStateChange]);

  useEffect(() => {
    onSaveRequestChange(() => async () => {
      const ok = await formMethods.trigger();
      if (!ok) {
        // Otherwise the save refuses with the offending field off screen.
        const errorTab = getLandingPageErrorTab(formMethods.formState.errors);
        if (errorTab !== null) {
          setActiveTab(errorTab);
        }

        return null;
      }

      const values = formMethods.getValues();

      return {
        values,
        reset: () => formMethods.reset(values),
      };
    });
  }, [formMethods, onSaveRequestChange]);

  return (
    <GenericForm<LandingPageFormValues> formMethods={formMethods} gap={3}>
      <LandingPageTabs readOnly={readOnly} activeTab={activeTab} onTabChange={setActiveTab} />
    </GenericForm>
  );
};
